import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/GridLegacy';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, FormControl, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails, IconButton } from '@mui/material';
import { Schedule, ExpandMore, CloudUpload, LocalOffer, Add, Delete, AccountBalanceWallet, RestaurantMenu } from '@mui/icons-material';
import { DIALOG_CONTENT_SX } from '../../../theme';
import CategoryIconByName from '../../../components/common/CategoryIconByName';
import { getImageDisplayUrl } from '../../../config/api';
import { toDateKey } from '../../../utils/dates';

function vibeAndPrimaryTagPool(tags) {
  if (!Array.isArray(tags)) return [];
  return tags.filter((t) => t.isActive !== false && (t.tagType === 'vibe' || t.tagType == null));
}
function famousTagOptions(tags) {
  if (!Array.isArray(tags)) return [];
  return tags.filter((t) => t.isActive !== false && t.tagType === 'famous');
}

export default function PlaceDialog({
  open,
  onClose,
  form,
  setForm,
  uploadedImages,
  mainImageIndex,
  categories = [],
  tags = [],
  uploadingImages,
  onImageUpload,
  onRemoveImage,
  onSetMainImage,
  menuImages = [],
  uploadingMenuImages,
  onMenuImagesUpload,
  onRemoveMenuImage,
  onSubmit,
  onAddBalance,
}) {
  const [topUpAmount, setTopUpAmount] = useState('');
  const [addingBalance, setAddingBalance] = useState(false);
  const topUpValue = Number(topUpAmount);
  const topUpValid = Number.isFinite(topUpValue) && topUpValue > 0;
  const handleAddBalance = async () => {
    if (!onAddBalance || !topUpValid || addingBalance) return;
    setAddingBalance(true);
    try {
      const data = await onAddBalance(topUpValue);
      if (data) setTopUpAmount('');
    } finally {
      setAddingBalance(false);
    }
  };
  useEffect(() => {
    if (!open) setTopUpAmount('');
  }, [open]);

  const coreOk = Boolean(
    form.name
    && form.description
    && (form.category || form.categories?.[0])
    && (form.address || form.location?.address)
    && (form.googleMapsLink || form.location?.googleMapsLink)
  );
  /** Create and update: at least one image always (matches submit handler and API). */
  const canSubmit = coreOk && uploadedImages.length > 0;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' } }}>
      <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700, py: 3, px: 4 }}>
        {form._id ? 'Edit Place' : 'Create New Place'}
      </DialogTitle>
      <DialogContent sx={DIALOG_CONTENT_SX}>
        <Grid container spacing={3} sx={{ pt: 1 }}>
          <Grid item xs={12}><Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 0 }}>Basic Information</Typography></Grid>
          {form._id && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Venue portal owner (contributor)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This user’s account sees this place in the venue dashboard and can confirm redemptions. Open the <strong>Users</strong> tab — each card shows <strong>User ID (for venue contributor)</strong> with a copy button — paste that id below, then save the place.
                </Typography>
                <TextField
                  fullWidth
                  label="Contributor user ID"
                  value={form.contributorId || ''}
                  onChange={(e) => setForm({ ...form, contributorId: e.target.value.replace(/\s/g, '') })}
                  placeholder="507f1f77bcf86cd799439011"
                  helperText="Must match a real user. Leave unchanged if already correct."
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Place Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Address" value={form.address || form.location?.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="e.g., 123 Main St, Suite 100" required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Google Maps Link" value={form.googleMapsLink || form.location?.googleMapsLink || ''} onChange={(e) => setForm({ ...form, googleMapsLink: e.target.value })} placeholder="https://maps.google.com/..." required helperText="Required for place verification" sx={{ '& .MuiFormHelperText-root': { mt: 0.5 } }} />
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Box sx={{ p: 2.5, borderRadius: 2, background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.05) 0%, rgba(0, 242, 254, 0.05) 100%)', border: '1px solid rgba(79, 172, 254, 0.1)', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 0.5 }}>Contact Information</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Phone and email are optional.</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Phone Number (Optional)" value={form.contactInfo?.phoneNumber || ''} onChange={(e) => setForm({ ...form, contactInfo: { ...form.contactInfo, phoneNumber: e.target.value } })} placeholder="e.g., +1 (555) 123-4567" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email (Optional)" type="email" value={form.contactInfo?.email || ''} onChange={(e) => setForm({ ...form, contactInfo: { ...form.contactInfo, email: e.target.value } })} placeholder="e.g., contact@example.com" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </Grid>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}><RestaurantMenu sx={{ mr: 1 }} /><Typography>Menu Photos (Optional)</Typography></AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload photos of the venue&apos;s menu or price list. These appear in a &quot;Menu&quot; section on the place details page in the app.
                </Typography>
                <Button variant="outlined" component="label" startIcon={<CloudUpload />} disabled={Boolean(uploadingMenuImages)} sx={{ mb: 2 }}>
                  {uploadingMenuImages ? 'Uploading…' : 'Upload Menu Photos'}
                  <input id="menu-images-upload" hidden accept="image/*" multiple type="file" onChange={onMenuImagesUpload} />
                </Button>
                {menuImages.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {menuImages.map((url, index) => (
                      <Box key={`${url}-${index}`} sx={{ position: 'relative' }}>
                        <Box component="img" src={getImageDisplayUrl(url)} alt={`Menu ${index + 1}`} sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }} />
                        <IconButton size="small" onClick={() => onRemoveMenuImage(index)} sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'error.light', color: 'white' } }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Comment (Optional)" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Additional comments about this place..." />
          </Grid>
          <Grid item xs={12} sx={{ mt: 0.5 }}>
            <Box sx={{ p: 2.5, borderRadius: 2, background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.05) 0%, rgba(0, 242, 254, 0.05) 100%)', border: '1px solid rgba(79, 172, 254, 0.1)', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 0.5 }}>Categories & Tags</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Select category and pricing information</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Category *</Typography>
            <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <Select value={form.category || form.categories?.[0] || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} displayEmpty renderValue={(selected) => { if (!selected) return <em style={{ color: '#999' }}>Select a category</em>; const category = categories.find((c) => c._id === selected || c.name === selected); return category ? category.name : selected; }}>
                <MenuItem value="" disabled><em>Select a category</em></MenuItem>
                {categories.filter((c) => c.isActive).map((category) => (
                  <MenuItem key={category._id} value={category.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: category.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        {category.icon ? <CategoryIconByName iconName={category.icon} size={16} color="white" /> : <span style={{ fontSize: 12, fontWeight: 600 }}>{category.name.charAt(0).toUpperCase()}</span>}
                      </Box>
                      {category.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Price Range</Typography>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <Select value={form.priceRange || 'mid'} onChange={(e) => setForm({ ...form, priceRange: e.target.value })} displayEmpty renderValue={(selected) => { if (!selected) return <em style={{ color: '#999' }}>Select price range</em>; const labels = { free: 'Free', low: 'Low ($)', mid: 'Mid ($$)', high: 'High ($$$)' }; return labels[selected] || selected; }}>
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="low">Low ($)</MenuItem>
                <MenuItem value="mid">Mid ($$)</MenuItem>
                <MenuItem value="high">High ($$$)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Primary Tag (For Recommendations)</Typography>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <Select value={form.primaryTag || ''} onChange={(e) => setForm({ ...form, primaryTag: e.target.value })} displayEmpty renderValue={(selected) => { if (!selected) return <em style={{ color: '#999' }}>Select primary tag</em>; const t = (tags || []).find((x) => x.slug === selected); return t ? t.name : selected; }}>
                <MenuItem value=""><em>None</em></MenuItem>
                {vibeAndPrimaryTagPool(tags || []).map((tag) => <MenuItem key={tag._id} value={tag.slug}>{tag.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Famous Tag (Optional)</Typography>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <Select value={Array.isArray(form.famousTags) && form.famousTags.length > 0 ? form.famousTags[0] : ''} onChange={(e) => setForm({ ...form, famousTags: e.target.value ? [e.target.value] : [] })} displayEmpty renderValue={(selected) => { if (!selected) return <em style={{ color: '#999' }}>None</em>; const t = famousTagOptions(tags || []).find((x) => x.slug === selected); return t ? t.name : selected; }}>
                <MenuItem value=""><em>None</em></MenuItem>
                {famousTagOptions(tags || []).map((tag) => <MenuItem key={tag._id || tag.slug} value={tag.slug}>{tag.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Vibe Tags (Secondary)</Typography>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <Select multiple value={Array.isArray(form.vibeTags) ? form.vibeTags : []} onChange={(e) => setForm({ ...form, vibeTags: e.target.value })} displayEmpty renderValue={(selected) => { if (selected.length === 0) return <em style={{ color: '#999' }}>Select vibe tags</em>; return selected.map((slug) => { const t = (tags || []).find((x) => x.slug === slug); return t ? t.name : slug; }).join(', '); }}>
                {vibeAndPrimaryTagPool(tags || []).map((tag) => <MenuItem key={tag._id} value={tag.slug}>{tag.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Default commission (%)"
              inputProps={{ min: 0, max: 100, step: '0.1' }}
              value={form.commissionPercent ?? ''}
              onChange={(e) => setForm({ ...form, commissionPercent: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              helperText="Applied to this place's offers as a percentage of the final bill. An offer's own commission overrides it."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Default cashback (%)"
              inputProps={{ min: 0, max: 100, step: '0.1' }}
              value={form.cashbackPercent ?? ''}
              onChange={(e) => setForm({ ...form, cashbackPercent: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              helperText="Cashback customers earn at this place, as a percentage of the cash they pay. Blank = no cashback."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Points cap (%)"
              inputProps={{ min: 0, max: 100, step: '0.1' }}
              value={form.pointsCapPercent ?? ''}
              onChange={(e) => setForm({ ...form, pointsCapPercent: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              helperText="Maximum percentage of the bill that can be paid with points at this place. Blank = global default."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Marketing fee (LE)"
              inputProps={{ min: 0, step: '0.01' }}
              value={form.marketingFeeLe ?? ''}
              onChange={(e) => setForm({ ...form, marketingFeeLe: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              helperText="Flat marketing fee charged to this place per transaction, in LE. Blank = global default. 0 = no fee."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Marketing fee min bill (LE)"
              inputProps={{ min: 0, step: '0.01' }}
              value={form.marketingFeeMinBillLe ?? ''}
              onChange={(e) => setForm({ ...form, marketingFeeMinBillLe: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              helperText="Minimum bill (LE) for the fee to apply. Blank = global default. 0 = charge on any bill."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Marketing fee max (LE)"
              inputProps={{ min: 0, step: '0.01' }}
              value={form.marketingFeeMaxLe ?? ''}
              onChange={(e) => setForm({ ...form, marketingFeeMaxLe: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              helperText="Cap on the fee. Blank = global default. 0 = no cap."
            />
          </Grid>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}><LocalOffer sx={{ mr: 1 }} /><Typography>Offers (Optional)</Typography></AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Add offers for this place. Each offer needs a name, description, and dates. Commission and cashback are optional — leave them empty to use the place defaults.</Typography>
                {(form.offers || []).map((offer, index) => (
                  <Box key={offer.id || index} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle2">Offer {index + 1}</Typography>
                      <Button size="small" color="error" startIcon={<Delete />} onClick={() => setForm({ ...form, offers: (form.offers || []).filter((_, i) => i !== index) })}>Remove</Button>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Offer name" value={offer.name || ''} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], name: e.target.value }; setForm({ ...form, offers: o }); }} /></Grid>
                      <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Description" value={offer.description || ''} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], description: e.target.value }; setForm({ ...form, offers: o }); }} /></Grid>
                      <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Commission (%)" type="number" inputProps={{ min: 0, max: 100, step: '0.1' }} value={offer.commissionPercent ?? ''} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], commissionPercent: e.target.value === '' ? '' : parseFloat(e.target.value) }; setForm({ ...form, offers: o }); }} helperText="Optional % of the final bill (overrides the place default)" /></Grid>
                      <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Cashback (%)" type="number" inputProps={{ min: 0, max: 100, step: '0.1' }} value={offer.cashbackPercent ?? ''} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], cashbackPercent: e.target.value === '' ? '' : parseFloat(e.target.value) }; setForm({ ...form, offers: o }); }} helperText="Optional cashback % (overrides the place default)" /></Grid>
                      <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Code (optional)" value={offer.code || ''} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], code: e.target.value }; setForm({ ...form, offers: o }); }} /></Grid>
                      <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Max redemptions (optional)" type="number" inputProps={{ min: 1 }} value={offer.maxRedemptions ?? ''} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], maxRedemptions: e.target.value === '' ? '' : parseInt(e.target.value, 10) }; setForm({ ...form, offers: o }); }} /></Grid>
                      <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Start date" type="date" value={toDateKey(offer.startDate)} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], startDate: e.target.value }; setForm({ ...form, offers: o }); }} InputLabelProps={{ shrink: true }} /></Grid>
                      <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Expiry date" type="date" value={toDateKey(offer.expiryDate)} onChange={(e) => { const o = [...(form.offers || [])]; o[index] = { ...o[index], expiryDate: e.target.value }; setForm({ ...form, offers: o }); }} InputLabelProps={{ shrink: true }} /></Grid>
                    </Grid>
                  </Box>
                ))}
                <Button startIcon={<Add />} variant="outlined" size="small" onClick={() => setForm({ ...form, offers: [...(form.offers || []), { id: `offer-${Date.now()}`, name: '', description: '', commissionPercent: '', cashbackPercent: '', code: '', maxRedemptions: '', startDate: '', expiryDate: '' }] })} sx={{ mt: 1 }}>Add offer</Button>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}><AccountBalanceWallet sx={{ mr: 1 }} /><Typography>Cashback Wallet / Ledger</Typography></AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Each completed redemption credits this place's ledger with its commission and debits the cashback it funds. The minimum is a spending cap: the balance may reach it but not go below, so the place never runs out of cash. Redemptions update the balance automatically; you can also top it up manually below.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={form._id ? 6 : 12}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Minimum balance (floor)"
                      inputProps={{ step: '0.01', max: 0 }}
                      value={form.ledgerMinLe ?? ''}
                      onChange={(e) => setForm({ ...form, ledgerMinLe: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      helperText="Amount the place may owe on cashback before it is clamped, e.g. -200. Must be 0 or negative. Blank = global default."
                    />
                  </Grid>
                  {form._id && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Current balance (LE)"
                        value={form.ledgerBalanceLe ?? 0}
                        InputProps={{ readOnly: true }}
                        helperText="Credited on every completed redemption; can also be topped up below."
                      />
                    </Grid>
                  )}
                  {form._id && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                        <TextField
                          size="small"
                          type="number"
                          label="Add balance (LE)"
                          inputProps={{ min: 0, step: '0.01' }}
                          value={topUpAmount}
                          onChange={(e) => setTopUpAmount(e.target.value)}
                          sx={{ width: { xs: '100%', sm: 220 } }}
                          disabled={!onAddBalance || addingBalance}
                          helperText="Admin credit added to this place's cashback wallet."
                        />
                        <Button
                          variant="contained"
                          onClick={handleAddBalance}
                          disabled={!onAddBalance || !topUpValid || addingBalance}
                          sx={{ height: 40, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                        >
                          {addingBalance ? 'Adding…' : 'Add balance'}
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}><Schedule sx={{ mr: 1 }} /><Typography>Opening Hours (Optional)</Typography></AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {Object.entries(form.openingHours || {}).map(([day, hours]) => (
                    <Grid item xs={12} sm={6} md={4} key={day}>
                      <Typography variant="subtitle2" gutterBottom>{day.charAt(0).toUpperCase() + day.slice(1)}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField size="small" label="Open" type="time" value={hours.open} onChange={(e) => setForm({ ...form, openingHours: { ...form.openingHours, [day]: { ...hours, open: e.target.value } } })} InputLabelProps={{ shrink: true }} />
                        <TextField size="small" label="Close" type="time" value={hours.close} onChange={(e) => setForm({ ...form, openingHours: { ...form.openingHours, [day]: { ...hours, close: e.target.value } } })} InputLabelProps={{ shrink: true }} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12}><Typography variant="h6" gutterBottom>Images</Typography></Grid>
          <Grid item xs={12}>
            <input accept="image/*" style={{ display: 'none' }} id="image-upload" multiple type="file" onChange={onImageUpload} disabled={uploadingImages} />
            <label htmlFor="image-upload">
              <Button variant="outlined" component="span" startIcon={<CloudUpload />} disabled={uploadingImages} sx={{ mb: 2, py: 1.5, px: 3, borderRadius: 2 }}>{uploadingImages ? 'Uploading...' : 'Upload Images'}</Button>
            </label>
          </Grid>
          {uploadedImages.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Uploaded Images ({uploadedImages.length})</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {uploadedImages.map((image, index) => (
                  <Box key={index} sx={{ position: 'relative' }}>
                    <Box component="img" src={typeof image === 'string' ? getImageDisplayUrl(image) : image} alt={`Image ${index + 1}`} onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23e0e0e0" width="100" height="100"/%3E%3C/svg%3E'; }} sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 2, border: mainImageIndex === index ? '3px solid' : '1px solid', borderColor: mainImageIndex === index ? 'primary.main' : 'divider', display: 'block', bgcolor: 'action.hover' }} />
                    <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="contained" color={mainImageIndex === index ? 'primary' : 'default'} onClick={() => onSetMainImage(index)} sx={{ minWidth: 'auto', p: 0.5 }}>{mainImageIndex === index ? '★' : '☆'}</Button>
                      <Button size="small" variant="contained" color="error" onClick={() => onRemoveImage(index)} sx={{ minWidth: 'auto', p: 0.5 }}>×</Button>
                    </Box>
                    {mainImageIndex === index && <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>Main Image</Typography>}
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3 }}>Cancel</Button>
        <Button variant="contained" onClick={onSubmit} disabled={!canSubmit} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)', borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3, '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)', boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)' }, '&:disabled': { background: 'rgba(0, 0, 0, 0.12)' } }}>{form._id ? 'Update Place' : 'Create Place'}</Button>
      </DialogActions>
    </Dialog>
  );
}
